import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Stack,
  Card,
  CardContent,
  // Switch,
  // FormControlLabel,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { pageApi, templateApi } from '../../services/api';
import EditorJSComponent from '../../components/editor/EditorJS';
import JsonContentEditor from '../../lib/jce/JsonContentEditor';
import MediaSelector from '../../components/media/MediaSelector';
import SeoEditor from '../../components/cms/SeoEditor';
import type { Page, Template } from '../../models';
import type { OutputData } from '@editorjs/editorjs';
import type { RJSFSchema } from "@rjsf/utils";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`page-tabpanel-${index}`}
      aria-labelledby={`page-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PageEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewPage = !id;
  const isViewMode = location.pathname.includes('/view');
  
  const [page, setPage] = useState<Partial<Page>>({
    title: '',
    slug: '',
    template: null,
    hero_image: null,
    sidebar_blocks: [],
    qnas: [],
    cards: [],
    content_blocks: [],
    seo: {
      meta_title: '',
      meta_description: '',
      meta_image: '',
      meta_robots: '',
      meta_viewport: '',
      canonical_url: '',
      keywords: '',
      structured_data: {},
      meta_social: []
    },
    status: 'draft',
  });

  const [templates, setTemplates] = useState<Template[]>([]);
  const [schemas, setSchemas] = useState<{
    qna: RJSFSchema;
    card: RJSFSchema;
  } | null>(null);
  const [loading, setLoading] = useState(!isNewPage);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Editor data states
  const [contentEditorData, setContentEditorData] = useState<OutputData>({ blocks: [] });
  const [sidebarEditorData, setSidebarEditorData] = useState<OutputData>({ blocks: [] });

  const [allPages, setAllPages] = useState<Page[]>([]);
  const [currentLang, setCurrentLang] = useState<'en' | 'hi'>('en');

  const getLocalizedValue = (field: keyof Page, lang: 'en' | 'hi' = currentLang) => {
    const key = `${field}_${lang}` as keyof Page;
    return page?.[key] !== undefined ? page?.[key] : page?.[field];
  };

  const updateLocalizedValue = (field: keyof Page, value: any) => {
    const key = `${field}_${currentLang}` as keyof Page;
    setPage((prev) => ({ ...prev, [key]: value }));
  };

  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLang: 'en' | 'hi' | null,
  ) => {
    if (newLang !== null && newLang !== currentLang) {
      // Sync current editor data to the current language field
      const currentContentBlocksKey = `content_blocks_${currentLang}` as keyof Page;
      const currentSidebarBlocksKey = `sidebar_blocks_${currentLang}` as keyof Page;
      
      setPage(prev => ({
        ...prev,
        [currentContentBlocksKey]: contentEditorData.blocks,
        [currentSidebarBlocksKey]: sidebarEditorData.blocks,
      }));

      // Load the new language data
      const newContentBlocksKey = `content_blocks_${newLang}` as keyof Page;
      const newSidebarBlocksKey = `sidebar_blocks_${newLang}` as keyof Page;
      const newContentBlocks = page?.[newContentBlocksKey] || [];
      const newSidebarBlocks = page?.[newSidebarBlocksKey] || [];
      
      setContentEditorData({ blocks: newContentBlocks });
      setSidebarEditorData({ blocks: newSidebarBlocks });
      
      setCurrentLang(newLang);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchSchemas();
    fetchAllPages();
    if (id) {
      fetchPage();
    }
  }, [id]);

  const fetchTemplates = async () => {
    try {
      const response = await templateApi.list();
      setTemplates(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showNotification('Failed to fetch templates', 'error');
    }
  };

  const fetchSchemas = async () => {
    try {
      const response = await pageApi.schemas();
      setSchemas(response.data);
    } catch (error) {
      console.error('Error fetching schemas:', error);
      showNotification('Failed to fetch schemas', 'error');
    }
  };

  const fetchAllPages = async () => {
    try {
      const response = await pageApi.list({ page_size: 1000 }); // adjust as needed
      setAllPages(response.data.results || response.data);
    } catch (error) {
      // handle error
    }
  };

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await pageApi.getById(parseInt(id!, 10));
      let pageData = response.data;
      
      // Only create draft if not in view mode
      if (!isViewMode && !pageData.is_draft) {
        try {
          const draftResponse = await pageApi.createDraft(pageData.id!);
          pageData = draftResponse.data;
          setPage(pageData);
        } catch (error) {
          console.error('Error handling draft:', error);
          showNotification('Failed to handle draft', 'error');
        }
      } else {
        setPage(pageData);
      }

      // Set editor data - load language-specific data if available, otherwise fallback to main field
      const contentBlocks = pageData[`content_blocks_${currentLang}` as keyof Page] || pageData.content_blocks || [];
      const sidebarBlocks = pageData[`sidebar_blocks_${currentLang}` as keyof Page] || pageData.sidebar_blocks || [];
      
      setContentEditorData({ blocks: contentBlocks as any });
      setSidebarEditorData({ blocks: sidebarBlocks as any });
    } catch (error) {
      console.error('Error fetching page:', error);
      showNotification('Failed to fetch page', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleContentEditorChange = (newData: OutputData) => {
    setContentEditorData(newData);
  };

  const handleSidebarEditorChange = (newData: OutputData) => {
    setSidebarEditorData(newData);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const pageToSave = {
        ...page,
        // Update the current language's content blocks from editor state
        [`content_blocks_${currentLang}`]: contentEditorData.blocks,
        [`sidebar_blocks_${currentLang}`]: sidebarEditorData.blocks,
        // Also update the main content_blocks field for backward compatibility/default behavior
        content_blocks: page.content_blocks_en || contentEditorData.blocks,
        sidebar_blocks: page.sidebar_blocks_en || sidebarEditorData.blocks,
        template: (page.template && typeof page.template === 'object') ? page.template?.id : page.template,
        parent: page.parent || null,
      };

      if (isNewPage) {
        await pageApi.create(pageToSave);
        showNotification('Page created successfully', 'success');
        navigate('/cms/pages');
      } else {
        await pageApi.update(page.id!, pageToSave);
        showNotification('Page saved successfully', 'success');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      showNotification(`Failed to ${isNewPage ? 'create' : 'save'} page`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await pageApi.publish(page.id!);
      showNotification('Page published successfully', 'success');
      navigate('/cms/pages');
    } catch (error) {
      console.error('Error publishing page:', error);
      showNotification('Failed to publish page', 'error');
    }
  };

  const handleUnpublish = async () => {
    try {
      await pageApi.unpublish(page.id!);
      showNotification('Page unpublished successfully', 'success');
      // Refresh page data
      const response = await pageApi.getById(page.id!);
      setPage(response.data);
    } catch (error) {
      console.error('Error unpublishing page:', error);
      showNotification('Failed to unpublish page', 'error');
    }
  };

  const handleImageSelect = (field: 'hero_image', value: number) => {
    setPage(prev => ({ ...prev, [field]: value }));
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Handle both cases: page.template could be a number (ID) or a Template object
  const selectedTemplate = typeof page.template === 'object' && page.template 
    ? page.template 
    : templates.find(t => t.id === page.template);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isNewPage && !page) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Page not found</Typography>
        <Button 
          component={Link} 
          to="/cms/pages" 
          variant="contained" 
          sx={{ mt: 2 }}
        >
          Back to Pages
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/cms/pages" underline="hover">
          Pages
        </MuiLink>
        <Typography color="text.primary">
          {isNewPage ? 'New Page' : (getLocalizedValue('title') || page?.title || `Page #${page?.id}`)}
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ mb: 3, p: 2, position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'background.paper' }}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <Box>
            {selectedTemplate && (
              <Chip 
                label={`Template: ${selectedTemplate.name}`} 
                color="primary" 
                size="small" 
                sx={{ mr: 1 }}
              />
            )}
            {page?.status === 'published' && (
              <Chip label="Published" color="success" size="small" />
            )}
            {page?.status === 'draft' && (
              <Chip label="Draft" color="warning" size="small" />
            )}
          </Box>
          
          <Stack direction="row" spacing={2}>
            <ToggleButtonGroup
              value={currentLang}
              exclusive
              onChange={handleLanguageChange}
              aria-label="language"
              size="small"
            >
              <ToggleButton value="en">English</ToggleButton>
              <ToggleButton value="hi">Hindi</ToggleButton>
            </ToggleButtonGroup>
            
            {isViewMode ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/cms/pages/${id}/edit`)}
                >
                  Edit Page
                </Button>
                {page?.status === 'published' && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleUnpublish}
                  >
                    Unpublish
                  </Button>
                )}
                {page?.status !== 'published' && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handlePublish}
                  >
                    Publish
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : (isNewPage ? 'Create Page' : 'Save Draft')}
                </Button>
                {!isNewPage && page?.status === 'draft' && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to discard this draft? This action cannot be undone.')) {
                        try {
                          await pageApi.discardDraft(page.id!);
                          navigate('/cms/pages');
                        } catch (err) {
                          showNotification('Failed to discard draft', 'error');
                        }
                      }
                    }}
                  >
                    Discard Draft
                  </Button>
                )}
                {!isNewPage && (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => {
                      window.open(`${import.meta.env.VITE_APP_URL}/${page.full_path}`, '_blank');
                    }}
                  >
                    Preview
                  </Button>
                )}
                {!isNewPage && page?.status !== 'published' && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handlePublish}
                  >
                    Publish
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Content" />
            <Tab label="SEO" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`Title (${currentLang.toUpperCase()})`}
                        value={getLocalizedValue('title') || ''}
                        onChange={(e) => updateLocalizedValue('title', e.target.value)}
                        disabled={isViewMode}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Slug"
                        value={page?.slug || ''}
                        onChange={(e) => setPage(prev => ({ ...prev, slug: e.target.value }))}
                        error={!!page?.slug_error}
                        helperText={page?.slug_error || 'Leave empty to auto-generate from title'}
                        disabled={isViewMode}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Template</InputLabel>
                        <Select
                          value={typeof page?.template === 'object' ? page?.template?.id || '' : page?.template || ''}
                          label="Template"
                          onChange={(e) => setPage(prev => ({ ...prev, template: Number(e.target.value) }))}
                          disabled={isViewMode}
                          required
                        >
                          {templates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Parent Page</InputLabel>
                        <Select
                          value={page.parent || ''}
                          label="Parent Page"
                          onChange={(e) => setPage(prev => ({ ...prev, parent: e.target.value ? Number(e.target.value) : null }))}
                          disabled={isViewMode}
                        >
                          <MenuItem value="">None (Top Level)</MenuItem>
                          {allPages
                            .filter(p => !id || p.id !== Number(id)) // Exclude self
                            .map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                {p.title} ({p.full_path || p.slug})
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Hero Image */}
            {selectedTemplate?.has_hero_image && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Hero Image</Typography>
                    <MediaSelector
                      value={page?.hero_image || 0}
                      onChange={(value: number) => handleImageSelect('hero_image', value)}
                      fileTypeFilter="images"
                      previewSize="medium"
                      disabled={isViewMode}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Q&A Section */}
            {selectedTemplate?.has_qnas && schemas?.qna && (
              <Grid item xs={12}>
                <JsonContentEditor
                  key={currentLang}
                  schema={schemas.qna}
                  data={(getLocalizedValue('qnas') as any) || []}
                  onChange={(data) => updateLocalizedValue('qnas', data)}
                  title={`Q&A Section (${currentLang.toUpperCase()})`}
                  description="Add questions and answers for this page"
                  disabled={isViewMode}
                  uiSchema={{
                    "items": {
                      "question":{
                        "ui:options": {
                          classNames: "ai11-jce-item ai11-jce-item-full-width",
                        },
                      },
                    },
                  }}
                />
              </Grid>
            )}

            {/* Cards Section */}
            {selectedTemplate?.has_cards && schemas?.card && (
              <Grid item xs={12}>
                <JsonContentEditor
                  key={currentLang}
                  schema={schemas.card}
                  data={(getLocalizedValue('cards') as any) || []}
                  onChange={(data) => updateLocalizedValue('cards', data)}
                  title={`Cards Section (${currentLang.toUpperCase()})`}
                  description="Add cards with titles, descriptions, and optional links"
                  disabled={isViewMode}
                />
              </Grid>
            )}

            {/* Main Content */}
            {selectedTemplate?.has_content && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Main Content ({currentLang.toUpperCase()})</Typography>
                    <EditorJSComponent
                      key={`content-${currentLang}`}
                      data={contentEditorData}
                      onChange={handleContentEditorChange}
                      disabled={isViewMode}
                      placeholder="Start writing your page content..."
                      minHeight={400}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Sidebar Content */}
            {selectedTemplate?.has_sidebar && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Sidebar Content ({currentLang.toUpperCase()})</Typography>
                    <EditorJSComponent
                      key={`sidebar-${currentLang}`}
                      data={sidebarEditorData}
                      onChange={handleSidebarEditorChange}
                      disabled={isViewMode}
                      placeholder="Add sidebar content..."
                      minHeight={300}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SeoEditor
            key={currentLang}
            seo={(getLocalizedValue('seo') as any) || {}}
            onChange={(seo) => updateLocalizedValue('seo', seo)}
            disabled={isViewMode}
          />
        </TabPanel>
      </Paper>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageEditor; 